import type {
  OpenWorldCompletedAttempt,
  OpenWorldGeneratedEntry,
  OpenWorldRuntimeState,
} from '@/lib/openWorlds/types'
import type {
  OpenWorldAttemptError,
  PlaythroughExecutionState,
} from '@/lib/openWorlds/attemptPersistence'

export type OpenWorldJournalKind = 'phase' | 'model' | 'tool'
export type OpenWorldJournalPhase = 'curator' | 'synthesis' | 'storyteller'
export type OpenWorldJournalStatus =
  | 'started'
  | 'completed'
  | 'skipped'
  | 'handled_error'
  | 'failed'
  | 'aborted'

export type OpenWorldJson =
  | null
  | boolean
  | number
  | string
  | OpenWorldJson[]
  | { [key: string]: OpenWorldJson }

export interface OpenWorldJournalRecord {
  sourceRecordId?: string | null
  id: string
  attemptId: string
  sequence: number
  kind: OpenWorldJournalKind
  phase: OpenWorldJournalPhase
  parentId: string | null
  name: string
  request: OpenWorldJson
  status: OpenWorldJournalStatus
  outcome: OpenWorldJson | null
  error: OpenWorldAttemptError | null
  usage: OpenWorldJson | null
  elapsedMs: number | null
  startedAt: string
  finishedAt: string | null
}

export interface StartOpenWorldJournalRequest {
  id: string
  attemptId: string
  executionToken: string
  sequence: number
  kind: OpenWorldJournalKind
  phase: OpenWorldJournalPhase
  parentId: string | null
  name: string
  request: OpenWorldJson
}

export interface StartOpenWorldJournalResponse {
  record: OpenWorldJournalRecord
  replayed: boolean
}

export interface FinishOpenWorldJournalRequest {
  id: string
  attemptId: string
  executionToken: string
  receiptId: string
  status: Exclude<OpenWorldJournalStatus, 'started'>
  outcome: OpenWorldJson | null
  error: OpenWorldAttemptError | null
  usage: OpenWorldJson | null
  elapsedMs: number
}

export interface FinishOpenWorldJournalResponse {
  record: OpenWorldJournalRecord
  replayed: boolean
}

export interface PublishOpenWorldAttemptRequest {
  attemptId: string
  executionToken: string
  receiptId: string
  storytellerMessage: string
  generatedEntries: OpenWorldGeneratedEntry[]
  stateAfter: OpenWorldRuntimeState
  diagnosticSummary: OpenWorldJson
}

export interface OpenWorldPublicationAcknowledgement {
  attemptId: string
  status: 'completed'
  finishedAt: string
  playthroughRevision: number
  activeHeadId: string
}

export interface PublishOpenWorldAttemptResponse {
  acknowledgement: OpenWorldPublicationAcknowledgement
  playthroughState: PlaythroughExecutionState
  replayed: boolean
}

export interface GetOpenWorldJournalResponse {
  items: OpenWorldJournalRecord[]
}

export interface GetOpenWorldActiveBranchResponse {
  attempts: OpenWorldCompletedAttempt[]
}

export const OPEN_WORLD_JOURNAL_LIMITS = {
  jsonBytes: 512 * 1024,
  nameBytes: 128,
  storytellerMessageBytes: 256 * 1024,
  diagnosticSummaryBytes: 64 * 1024,
  elapsedMsMax: 2_147_483_647,
} as const

type UnknownRecord = Record<string, unknown>
const encoder = new TextEncoder()
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const own = (value: UnknownRecord, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key)
const exact = (value: unknown, keys: readonly string[]): value is UnknownRecord =>
  isRecord(value) && Object.keys(value).length === keys.length && keys.every((key) => own(value, key))
const uuid = (value: unknown): value is string => typeof value === 'string' && UUID_RE.test(value)
const positiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= 1 && (value as number) <= 2_147_483_647
const bytes = (value: string): number => encoder.encode(value).byteLength

function isJson(value: unknown, seen = new Set<object>()): value is OpenWorldJson {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value !== 'object') return false
  if (seen.has(value)) return false
  seen.add(value)
  const valid = Array.isArray(value)
    ? value.every((item) => isJson(item, seen))
    : Object.getPrototypeOf(value) === Object.prototype &&
      Object.values(value as UnknownRecord).every((item) => isJson(item, seen))
  seen.delete(value)
  return valid
}

function jsonWithin(value: unknown, maximum = OPEN_WORLD_JOURNAL_LIMITS.jsonBytes): value is OpenWorldJson {
  if (!isJson(value)) return false
  try {
    return bytes(JSON.stringify(value)) <= maximum
  } catch {
    return false
  }
}

function safeError(value: unknown): value is OpenWorldAttemptError | null {
  return value === null || (
    exact(value, ['code', 'message']) &&
    typeof value.code === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(value.code) &&
    typeof value.message === 'string' && bytes(value.message) <= 4096
  )
}

export function validateStartOpenWorldJournalRequest(value: unknown): value is StartOpenWorldJournalRequest {
  if (!exact(value, [
    'id', 'attemptId', 'executionToken', 'sequence', 'kind', 'phase', 'parentId', 'name', 'request',
  ])) return false
  return uuid(value.id) && uuid(value.attemptId) && uuid(value.executionToken) &&
    positiveInteger(value.sequence) && ['phase', 'model', 'tool'].includes(value.kind as string) &&
    ['curator', 'synthesis', 'storyteller'].includes(value.phase as string) &&
    (value.parentId === null || uuid(value.parentId)) && typeof value.name === 'string' &&
    value.name.length > 0 && bytes(value.name) <= OPEN_WORLD_JOURNAL_LIMITS.nameBytes &&
    jsonWithin(value.request)
}

export function validateFinishOpenWorldJournalRequest(value: unknown): value is FinishOpenWorldJournalRequest {
  if (!exact(value, [
    'id', 'attemptId', 'executionToken', 'receiptId', 'status', 'outcome', 'error', 'usage', 'elapsedMs',
  ])) return false
  const terminal = ['completed', 'skipped', 'handled_error', 'failed', 'aborted'].includes(value.status as string)
  const elapsed = Number.isInteger(value.elapsedMs) && (value.elapsedMs as number) >= 0 &&
    (value.elapsedMs as number) <= OPEN_WORLD_JOURNAL_LIMITS.elapsedMsMax
  return uuid(value.id) && uuid(value.attemptId) && uuid(value.executionToken) && uuid(value.receiptId) &&
    terminal && jsonWithin(value.outcome) && safeError(value.error) && jsonWithin(value.usage) && elapsed &&
    (['failed', 'aborted', 'handled_error'].includes(value.status as string) ? value.error !== null : true)
}

export function validatePublishOpenWorldAttemptRequest(value: unknown): value is PublishOpenWorldAttemptRequest {
  if (!exact(value, [
    'attemptId', 'executionToken', 'receiptId', 'storytellerMessage', 'generatedEntries',
    'stateAfter', 'diagnosticSummary',
  ])) return false
  return uuid(value.attemptId) && uuid(value.executionToken) && uuid(value.receiptId) &&
    typeof value.storytellerMessage === 'string' && value.storytellerMessage.trim().length > 0 &&
    bytes(value.storytellerMessage) <= OPEN_WORLD_JOURNAL_LIMITS.storytellerMessageBytes &&
    Array.isArray(value.generatedEntries) && jsonWithin(value.generatedEntries) &&
    isRecord(value.stateAfter) && jsonWithin(value.stateAfter) &&
    jsonWithin(value.diagnosticSummary, OPEN_WORLD_JOURNAL_LIMITS.diagnosticSummaryBytes)
}

export const isOpenWorldJournalRecord = isRecord
export const isOpenWorldJournalUuid = uuid
export const isOpenWorldJournalPositiveInteger = positiveInteger
export const isOpenWorldJournalJson = jsonWithin
export const isOpenWorldJournalSafeError = safeError
